import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAwards } from "../../api/awards";
import { fetchWorkLogs } from "../../api/workLogs";
import { useAuth } from "../../hooks/useAuth";
import Panel from "./Panel";
import StatCard from "./StatCard";
import WorkLogPanel from "./WorkLogPanel";
import { WORK_LOG_COPY } from "./workLogCopy";

const formatDate = (value) => {
    if (!value) return "TBD";
    // work_date is a plain YYYY-MM-DD; split it rather than going through Date,
    // which would shift the day in negative-offset timezones.
    const [y, m, d] = value.split("-");
    return `${m}/${d}/${y}`;
};

// Interns and instructors share this dashboard; each program keeps its own logs,
// wording and history page.
export default function InternshipDashboard({ program = "internship" }) {
    const { user, isAuthenticated } = useAuth();
    const copy = WORK_LOG_COPY[program];

    // Same key WorkLogPanel uses, so react-query serves both from one cache
    // entry and the panel's mutations refresh these stats too.
    const { data: workLogs = [] } = useQuery({
        queryKey: ["workLogs", program],
        queryFn: () => fetchWorkLogs(program),
        enabled: isAuthenticated,
    });

    const { data: allAwards = [] } = useQuery({
        queryKey: ["awards"],
        queryFn: fetchAwards,
        enabled: isAuthenticated,
    });

    // Work (in WorkLogPanel) holds what still needs review; approved entries
    // move to History below.
    const approved = workLogs.filter((log) => log.status === "approved");

    const workingTime = approved.reduce((acc, log) => acc + Number(log.hours), 0);
    const daysWorked = new Set(approved.map((log) => log.work_date)).size;

    const myAwardIds = user?.awards || [];
    const myAwards = allAwards.filter((award) => myAwardIds.includes(award.pk));

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-[84px] gap-y-[38px] max-w-[1528px] h-full">
            {/* Left column */}
            <div className="flex flex-col gap-[38px] min-h-0">
                {/* Stat cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-[56px] shrink-0">
                    <StatCard
                        label={copy.hoursLabel}
                        value={Math.round(workingTime * 10) / 10}
                        unit="hr"
                    />
                    <StatCard label={copy.daysLabel} value={daysWorked} unit="day" />
                </div>

                <WorkLogPanel
                    program={program}
                    menuTo={copy.historyPath}
                    className="bg-[#ffeec6] min-h-[367px] xl:min-h-0 xl:flex-1"
                />
            </div>

            {/* Right column — panels grow proportionally to their design
                heights (254 : 296) so the ratio survives any leftover space. */}
            <div className="flex flex-col gap-[38px] min-h-0">
                <Panel title="Awards" className="bg-[#d2d4d7] min-h-[254px] xl:min-h-0 xl:flex-[254_1_0%]">
                    <div className="flex-1 overflow-y-auto">
                        {myAwards.length > 0 ? (
                            myAwards.map((award) => (
                                <div
                                    key={award.pk}
                                    className="flex items-center h-[70px] pl-[28px] text-[24px] text-[#5e5e5e]"
                                >
                                    <span className="truncate">{award.title}</span>
                                </div>
                            ))
                        ) : (
                            <p className="pl-[28px] pt-[26px] text-[20px] text-[#777f88]">
                                No awards yet.
                            </p>
                        )}
                    </div>
                </Panel>

                <Panel
                    title="History"
                    menuTo={copy.historyPath}
                    className="bg-[#e2dcd7] min-h-[296px] xl:min-h-0 xl:flex-[296_1_0%]"
                >
                    <div className="grid grid-cols-[1fr_140px_80px] gap-x-4 pt-[16px] text-[16px] text-[#777f88] tracking-[0.8px]">
                        <span className="pl-[28px]">{copy.column}</span>
                        <span>Date</span>
                        <span>Hours</span>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {approved.length > 0 ? (
                            approved.map((log) => (
                                <div
                                    key={log.id}
                                    className="grid grid-cols-[1fr_140px_80px] gap-x-4 items-center h-[70px] text-[24px] text-[#5e5e5e]"
                                >
                                    <span className="pl-[28px] truncate">{log.description}</span>
                                    <span>{formatDate(log.work_date)}</span>
                                    <span>{Number(log.hours)}</span>
                                </div>
                            ))
                        ) : (
                            <p className="pl-[28px] pt-[26px] text-[20px] text-[#777f88]">
                                {copy.noApproved}
                            </p>
                        )}
                    </div>
                </Panel>
            </div>
        </div>
    );
}
